import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { VendorCard } from "@/components/common/VendorCard";
import { CardSkeletons, EmptyState, ErrorState } from "@/components/common/States";
import { Store } from "lucide-react";
import { cn } from "@/lib/utils";

export default function CustomerHome() {
  const [category, setCategory] = useState(null);

  const cats = useQuery({ queryKey: ["categories"], queryFn: () => api("/categories") });
  const stats = useQuery({ queryKey: ["hero-stats"], queryFn: () => api("/site/hero-stats") });
  const vendors = useQuery({
    queryKey: ["vendors", category],
    queryFn: () => api(`/vendors${category ? `?category=${encodeURIComponent(category)}` : ""}`),
  });

  return (
    <div className="space-y-8">
      {/* Hero */}
      <section className="overflow-hidden rounded-3xl border-2 border-foreground/10 shadow-pop-sm">
        <div className="stripe-green h-4 w-full" />
        <div className="relative bg-popblue p-8 text-white lg:p-12">
          <div className="max-w-2xl">
            <span className="inline-block -rotate-2 rounded-xl bg-popyellow px-4 py-1.5 font-display text-base text-foreground shadow-pop-sm">
              Grown local · Picked for you
            </span>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] lg:text-5xl">
              Fresh from your neighbourhood, delivered to your door.
            </h1>
            <p className="mt-3 text-lg text-white/85">
              Support local bakeries, cafés and grocers across South Africa.
            </p>
            {stats.data?.length > 0 && (
              <div className="mt-7 flex flex-wrap gap-3">
                {stats.data.map((s) => (
                  <div key={s.key} className="rounded-2xl bg-white px-4 py-2 text-foreground shadow-pop-sm">
                    <p className="font-display text-2xl font-bold text-primary">{s.value}</p>
                    <p className="text-xs text-muted-foreground">{s.label_en}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="checker checker-red checker-sm h-6 w-full" />
      </section>

      {/* Categories */}
      <section>
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => setCategory(null)}
            data-testid="category-all"
            className={cn(
              "shrink-0 rounded-full border-2 px-4 py-2 text-sm font-medium transition-colors",
              !category ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted",
            )}
          >
            All
          </button>
          {cats.data?.map((c) => (
            <button
              key={c.category_id}
              onClick={() => setCategory(c.name)}
              data-testid={`category-${c.name}`}
              className={cn(
                "shrink-0 rounded-full border-2 px-4 py-2 text-sm font-medium transition-colors",
                category === c.name ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted",
              )}
            >
              {c.name}
              <span className="ml-1.5 opacity-60">{c.vendor_count}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Vendors */}
      <section>
        <h2 className="mb-4 font-display text-xl">{category || "All stores"}</h2>
        {vendors.isLoading ? (
          <CardSkeletons />
        ) : vendors.isError ? (
          <ErrorState onRetry={vendors.refetch} />
        ) : vendors.data?.length === 0 ? (
          <EmptyState
            icon={Store}
            title="No local stores here yet"
            description="Check back soon — new vendors join Oppad every week."
            action={
              <Link to="/become-vendor" className="text-sm text-primary hover:underline">
                Open your own store
              </Link>
            }
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {vendors.data.map((v) => (
              <VendorCard key={v.vendor_id} vendor={v} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
